import { createContext, useContext, useRef, useState } from 'react';

import styled from 'styled-components';

import { Button } from '@components/button';
import { IconButton } from '@components/icon-button';
import { IconAI } from '@components/icons/ai';
import { IconArrowRight } from '@components/icons/arrow-right';
import { IconInput } from '@components/icons/input';
import { IconReplace } from '@components/icons/replace';
import { IconTextFormatLink } from '@components/icons/text-format-link';
import { SizedRelativeSpinner } from '@components/loading-spinner/loading-spinner';
import { Modal, useModal } from '@components/modal';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import {
  FieldMappingGroup,
  findUnknownVariables,
  MappableVariable,
  mapVariableGroups,
  transformInput,
  useVariableStyledContent,
} from '@components/text-editors/addons/field-mapping';
import { TextAreaDynamic } from '@components/textarea/textarea-dynamic';
import { showError } from '@components/toaster';
import { useStickyAutoScroll } from '@hooks/use-sticky-auto-scroll';
import { useScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';
import { seconds } from '@utils/milliseconds';
import { uuid } from '@utils/uuid';

import { useAiInputAssistSendMessage } from './ai-input-assist-api';
import { AiAssistRoles, AiInputProps, ChatMessage } from './types';

export const useAiInputAssistModal = () => useModal(AiInputModal, { variant: 'EXTRA_LARGE' });

const GENERATING_ID = 'generating';
const TIMEOUT_DURATION = seconds(30);

let MESSAGE_STORE: Record<string, Array<ChatMessage>> = {};

const getCachedMessages = (uuid: string) => {
  const cachedMessages = uuid ? MESSAGE_STORE[uuid] ?? [] : [];
  return cachedMessages;
};

const VariableContext = createContext<Record<string, MappableVariable>>({});

type AiInputModalProps = AiInputProps & {
  variableGroups?: Array<FieldMappingGroup>;
  editorUuid?: keyof typeof MESSAGE_STORE;
  onSave: (value: string) => void;
  useWebSocket: (url: string, onMessage: (data: MessageEvent<string>) => void) => void;
  plaintext?: boolean;
};

const AiInputModal = ({
  onSave,
  threadInstructions,
  generateChoices,
  variableGroups,
  editorUuid = '',
  useWebSocket,
  plaintext,
}: AiInputModalProps) => {
  const { t } = useScopedTranslate(locale);
  const [value, setValue] = useState('');
  const [messages, setMessages] = useState<Array<ChatMessage>>(getCachedMessages(editorUuid));
  const currentMessageRef = useRef('');
  const [isLoading, setIsLoading] = useState(false);
  const chatContainerRef = useRef<HTMLDivElement | null>(null);
  const chatContentRef = useRef<HTMLDivElement | null>(null);
  const websocketId = useRef(uuid()).current;
  const lastMessage = useRef('');
  const lastPayload = useRef<{
    history: Array<Pick<ChatMessage, 'role' | 'message'>>;
    variableMap: Record<string, string>;
    threadInstructions: string;
    websocketId: string;
  } | null>(null);

  const { onScroll: handleChatScroll } = useStickyAutoScroll({
    containerRef: chatContainerRef,
    contentRef: chatContentRef,
    resetKey: editorUuid,
  });

  const sendMessage = useAiInputAssistSendMessage();

  const variables = mapVariableGroups(variableGroups);

  const variableMap = Object.keys(variables).reduce((acc, key) => {
    acc[key] = variables[key].label;
    return acc;
  }, {} as Record<string, string>);

  const toHistory = (messageList: Array<ChatMessage>) =>
    messageList
      .filter((message) => message.id !== GENERATING_ID && !!message.message)
      .map(({ role, message }) => ({ role, message }));

  const handleAddNewMessage = (newMessage: ChatMessage) => {
    setMessages((prevMessages) => {
      const parsedMessages = prevMessages.map((message) =>
        message.id === GENERATING_ID ? { ...newMessage, id: uuid() } : message
      );
      MESSAGE_STORE = { ...MESSAGE_STORE, [editorUuid]: parsedMessages };
      return parsedMessages;
    });
  };

  const addMessage = (message: string) => {
    lastMessage.current = message;
    setValue('');
    const newMessages: ChatMessage[] = [
      ...messages,
      { message, id: uuid(), role: 'user' as AiAssistRoles },
      { message: '', id: GENERATING_ID, role: 'assistant' as AiAssistRoles },
    ];
    setMessages(newMessages);
    MESSAGE_STORE = { ...MESSAGE_STORE, [editorUuid]: newMessages };
    return newMessages;
  };

  const handleSendMessage = (message?: string) => {
    if (isLoading) {
      return;
    }

    const content = (message ?? value).trim();
    if (!content) {
      return;
    }

    const currentMessageId = uuid();
    currentMessageRef.current = currentMessageId;
    const newMessages = addMessage(content);
    const payload = {
      history: toHistory(newMessages),
      variableMap,
      threadInstructions,
      websocketId,
    };
    lastPayload.current = payload;
    sendMessage(payload);
    setIsLoading(true);

    setTimeout(() => {
      if (currentMessageRef.current === currentMessageId) {
        setIsLoading(false);
        setMessages((prevMessages) => {
          const filteredMessages = prevMessages.filter((message) => message.id !== GENERATING_ID);
          MESSAGE_STORE = { ...MESSAGE_STORE, [editorUuid]: filteredMessages };
          return filteredMessages;
        });
        showError(t('timeout'));
      }
    }, TIMEOUT_DURATION);
  };

  const handleWebsocketMessage = (data: MessageEvent<string>) => {
    currentMessageRef.current = '';
    const AIMessage = JSON.parse(data.data);
    if (AIMessage.content.hasError) {
      showError(AIMessage.content.message);
      setIsLoading(false);
      return;
    }
    // Check if there are unknown variables in the AI message, and if it is, we try again, given we haven't done that yet
    const unknownVariables = findUnknownVariables(AIMessage.content.message, variables);
    if (unknownVariables.length > 0 && lastMessage.current && lastPayload.current) {
      sendMessage(lastPayload.current);
      lastMessage.current = '';
      return;
    }
    handleAddNewMessage(AIMessage.content);
    setIsLoading(false);
  };

  useWebSocket(`content-ai/assistant/${websocketId}/`, (data: MessageEvent<string>) => {
    handleWebsocketMessage(data);
  });

  return (
    <VariableContext.Provider value={variables}>
      <Modal>
        <Modal.SimpleHeader title={t('title')} icon={<IconAI color="aiGradientTeal100" />} />
        <StyledContent>
          <ChatContainer ref={chatContainerRef} onScroll={handleChatScroll}>
            <Stack ref={chatContentRef} space={4}>
              {messages.map((message) => (
                <Message
                  key={message.id}
                  message={message}
                  onSave={onSave}
                  isLoading={isLoading}
                  plaintext={plaintext}
                  variableGroups={variableGroups}
                />
              ))}
            </Stack>
          </ChatContainer>
          {messages.length === 0 && generateChoices && (
            <GenerateChoices>
              {generateChoices.map((choice) => (
                <Button
                  key={choice.title}
                  text={choice.title}
                  variant="secondary"
                  iconLeft={<IconReplace />}
                  onClick={() => handleSendMessage(choice.prompt)}
                />
              ))}
            </GenerateChoices>
          )}
          <InputContainer>
            <TextAreaDynamic
              autoFocus
              placeholder={t('aiPlaceholder')}
              onChange={setValue}
              value={value}
              style={{
                minHeight: pxToRem(INPUT_MIN_HEIGHT),
                maxHeight: pxToRem(256),
                paddingRight: pxToRem(56),
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && !event.shiftKey) {
                  event.preventDefault();
                  handleSendMessage();
                }
              }}
            />
            <SendButtonSlot>
              <IconButton
                icon={<IconArrowRight />}
                variant="blue-white"
                onClick={() => handleSendMessage(value)}
                disabled={!value || isLoading}
              />
            </SendButtonSlot>
          </InputContainer>
        </StyledContent>
      </Modal>
    </VariableContext.Provider>
  );
};

const StyledContent = styled(Modal.Content)({
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  gap: pxToRem(16),
});

// Sits outside ChatContainer so it stays with the input at the bottom of the
// modal instead of floating at the top of the empty chat area.
const GenerateChoices = styled.div({
  display: 'flex',
  justifyContent: 'flex-end',
});

const ChatContainer = styled.div({
  flex: 1,
  overflow: 'auto',
  whiteSpace: 'pre-wrap',
  paddingTop: pxToRem(16),
  // Using justify-content: flex-end doesn't work well with scrollbars, so we use margin-top: auto instead
  '&>:first-child': {
    marginTop: 'auto',
  },
});

const InputContainer = styled.div({
  position: 'relative',
  textarea: {
    paddingTop: pxToRem(16),
  },
});

const INPUT_MIN_HEIGHT = 58;
const SEND_BUTTON_SIZE = 40; // IconButton size="lg"

// Centres the button in the input, which is what the inset is for - both edges
// so it sits square in the corner once the input grows past its min height.
const SEND_BUTTON_INSET = pxToRem((INPUT_MIN_HEIGHT - SEND_BUTTON_SIZE) / 2);

// Positioning the IconButton directly would land on its inner <button>, leaving
// the disabled-state wrapper span in flow to add an empty 24px line box.
const SendButtonSlot = styled.div({
  position: 'absolute',
  right: SEND_BUTTON_INSET,
  bottom: SEND_BUTTON_INSET,
  display: 'flex',
});

type ChatMessageProps = Pick<AiInputModalProps, 'onSave' | 'plaintext' | 'variableGroups'> & {
  message: ChatMessage;
  isLoading: boolean;
};

const Message = (props: ChatMessageProps) => {
  const { message, isLoading } = props;
  if (message.id === GENERATING_ID && isLoading) {
    return <GeneratingMessage />;
  }
  if (message.role === 'assistant') {
    return <TokenedMessageHandler {...props} />;
  }
  return <MessageContainer>{message.message}</MessageContainer>;
};

const TokenedMessageHandler = ({
  message,
  onSave,
  plaintext,
  variableGroups,
}: ChatMessageProps) => {
  const variables = useContext(VariableContext);
  const { t } = useScopedTranslate(locale);

  const { cleanMessage, styledContent } = useVariableStyledContent({
    content: message.message,
    variables,
    variableGroups,
    icon: IconTextFormatLink,
    hideRemove: true,
    includeFullAttrs: false,
  });

  const handleSave = () => {
    const parsedText = plaintext ? cleanMessage : cleanMessage.replace(/\n/g, '<br/>');
    const transformed = transformInput({ value: parsedText, variables });

    onSave(transformed.value);
  };

  return (
    <MessageContainer $aiGradient={message.role === 'assistant'}>
      <Stack space={2}>
        {styledContent}
        <Stack orientation="horizontal" justify="flex-end" align="center" space={4}>
          <Text font="semibold14" aiGradient text={t('aiOutput')} />
          <Button
            variant="secondary"
            iconRight={<IconInput />}
            text={t('placeContent')}
            onClick={() => handleSave()}
          />
        </Stack>
      </Stack>
    </MessageContainer>
  );
};

const GeneratingMessage = () => {
  const { t } = useScopedTranslate(locale);

  return (
    <MessageContainer style={{ display: 'flex', justifyContent: 'center' }}>
      <Stack orientation="horizontal" space={4} align="center">
        <div>
          <SizedRelativeSpinner size={24} />
        </div>
        <Text aiGradient text={t('generating')} font="semibold14" />
      </Stack>
    </MessageContainer>
  );
};

const MessageContainer = styled.div<{ $aiGradient?: boolean }>(({ $aiGradient }) => {
  const borderRadius = pxToRem(8);
  return {
    borderRadius,
    border: `${pxToRem(1)} solid ${theme.color.border10}`,
    padding: pxToRem(16),
    ...($aiGradient && {
      position: 'sticky',
      '&&:before': {
        content: '""',
        position: 'absolute',
        inset: 0,
        zIndex: -1,
        padding: pxToRem(1), // the border thickness
        borderRadius,
        background: theme.gradient.ai,
        mask: `
        linear-gradient(#000 0 0) exclude,
        linear-gradient(#000 0 0) content-box
      `,
        WebkitMask: `
        linear-gradient(#000 0 0) exclude,
        linear-gradient(#000 0 0) content-box
      `,
      },
    }),
  };
});

const locale = {
  en: {
    title: 'Journey Helper',
    aiPlaceholder:
      'Type a short summary of what you want to say, and I will create a ready-to-use message',
    chats: 'Chats',
    newChat: 'New chat',
    generating: 'Generating',
    aiOutput: 'AI Output',
    placeContent: 'Place content',
    timeout: 'Request timed out. Please try again.',
  },
};
